// src/components/auth/SecureAuthChoice.jsx
// Component for choosing between MFA and Biometrics setup for branch admins

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Shield, Fingerprint, Loader2, LogOut } from 'lucide-react';
import { cn } from '@/lib/utils';

export default function SecureAuthChoice({ 
  isOpen, 
  onChooseMFA, 
  onChooseBiometrics,
  onLogout,
  biometricAvailable = true 
}) {
  const [selected, setSelected] = useState(null);

  // Reset selection when dialog opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelected(null);
    }
  }, [isOpen]);

  const handleMFA = () => {
    setSelected('mfa');
    onChooseMFA();
  };

  const handleBiometrics = () => {
    setSelected('biometrics');
    onChooseBiometrics();
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-md w-[calc(100vw-2rem)] max-w-md p-4 sm:p-6 overflow-hidden"
        hideCloseButton
        overlayClassName={cn(
          "transition-all duration-500 ease-in-out backdrop-blur-md"
        )}
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="text-center">
          <div className="mx-auto mb-4">
            <div className="h-16 w-16 bg-blue-100 rounded-full flex items-center justify-center">
              <Shield className="h-8 w-8 text-blue-600" />
            </div>
          </div>
          <DialogTitle className="text-lg sm:text-xl break-words">
            Wybierz metodę bezpiecznego logowania
          </DialogTitle>
          <DialogDescription className="text-center pt-2 break-words text-xs leading-relaxed px-1">
            Jako administrator musisz skonfigurować co najmniej jedną metodę bezpiecznego logowania dla zwiększenia bezpieczeństwa konta.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-4 w-full box-border overflow-hidden">
          {/* MFA Option */}
          <Button
            onClick={handleMFA}
            disabled={selected !== null}
            className={cn(
              "w-full h-auto p-3 sm:p-4 flex items-start gap-3 sm:gap-4 justify-start box-border",
              "bg-white border-2 hover:bg-slate-50 transition-all",
              "overflow-hidden max-w-full",
              selected === 'mfa' 
                ? "border-blue-600 bg-blue-50" 
                : "border-slate-200 hover:border-blue-300"
            )}
          >
            <div className="h-10 w-10 sm:h-12 sm:w-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              {selected === 'mfa' ? (
                <Loader2 className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600 animate-spin" />
              ) : (
                <Shield className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
              )}
            </div>
            <div className="flex-1 text-left min-w-0 max-w-full overflow-hidden pr-1 sm:pr-2">
              <div className="font-semibold text-slate-900 break-words text-sm sm:text-base">Uwierzytelnianie dwuskładnikowe (2FA)</div>
              <div className="text-[10px] sm:text-xs text-slate-500 mt-0.5 break-words leading-relaxed hyphens-auto">
                Użyj aplikacji authenticator (Google Authenticator, Microsoft Authenticator)
              </div>
            </div>
          </Button>

          {/* Biometrics Option */}
          <Button
            onClick={handleBiometrics}
            disabled={selected !== null || !biometricAvailable}
            className={cn(
              "w-full h-auto p-3 sm:p-4 flex items-start gap-3 sm:gap-4 justify-start box-border",
              "bg-white border-2 hover:bg-slate-50 transition-all",
              "overflow-hidden max-w-full",
              !biometricAvailable && "opacity-50 cursor-not-allowed",
              selected === 'biometrics' 
                ? "border-green-600 bg-green-50" 
                : "border-slate-200 hover:border-green-300"
            )}
          >
            <div className="h-10 w-10 sm:h-12 sm:w-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
              {selected === 'biometrics' ? (
                <Loader2 className="h-5 w-5 sm:h-6 sm:w-6 text-green-600 animate-spin" />
              ) : (
                <Fingerprint className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
              )}
            </div>
            <div className="flex-1 text-left min-w-0 max-w-full overflow-hidden pr-1 sm:pr-2">
              <div className="font-semibold text-slate-900 break-words text-sm sm:text-base">Logowanie biometryczne / klucz dostępu</div>
              <div className="text-[10px] sm:text-xs text-slate-500 mt-0.5 break-words leading-relaxed hyphens-auto">
                {biometricAvailable 
                  ? "Użyj FaceID, TouchID, Windows Hello lub odcisku palca"
                  : "Niedostępne na tym urządzeniu"
                }
              </div>
            </div>
          </Button>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs sm:text-sm text-blue-700 break-words">
          <strong>Uwaga:</strong> Możesz skonfigurować obie metody lub wybrać jedną z nich. 
          W każdej chwili możesz zmienić ustawienia w sekcji Profil.
        </div>

        {/* Logout option */}
        <div className="pt-2 border-t">
          <Button
            onClick={onLogout}
            variant="ghost"
            className="w-full text-slate-500 hover:text-slate-700 hover:bg-slate-50"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Wyloguj się
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
